// Libraries
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import SelectField from 'src/components/HTKit/Forms/SelectField';
import cn from 'classnames';
import get from 'lodash/get';
// Components
import Icon from 'src/components/HTKit/Icon';
import { PanelV2 } from 'src/components/HTKit/Elements/PanelV2';
// Styles
import styles from '../styles.scss';

const PriceOrHintDisplay = ({ visible, value, hintText, hasModalHint, onHintClick }) => {
  if (!visible) return null;
  const showHintIcon = hasModalHint || hintText;
  const iconName = hasModalHint ? 'info-outline' : 'helper';
  return (
    <div className="flex alignItemsCenter">
      {value && <span className="p2 n700">{value}</span>}
      {showHintIcon && <Icon className={styles.hintIcon} onClick={onHintClick} name={iconName} />}
    </div>
  );
};

const CheckboxLabel = ({ className, checkboxValue, checkboxLabel, onClick, onChange, checked }) => {
  const panelRef = useRef(null);
  return (
    <div className={cn(styles.checkbox, className)} ref={panelRef}>
      <label className={styles.label}>
        <input
          type="checkbox"
          onChange={onChange}
          checked={checked}
          value={checkboxValue}
          onClick={onClick}
        />
        <div className={styles.check} />
        <div className={styles.labelText}>{checkboxLabel}</div>
      </label>
    </div>
  );
};

const DropdownArrow = () => (
  <div className={styles.selectArrow}>
    <Icon name="chevron" />
  </div>
);

const CheckboxPanel = ({
  hintText,
  showDropdown,
  dropdownClassName,
  onDropdownChange,
  className,
  answer,
  launchAdditionalInfoDisplay,
  ...props
}) => {
  const [showHint, setShowHint] = useState(false);
  const [checkboxIsChecked, setCheckboxIsChecked] = useState(false);
  const [dropdownValue, setDropdownValue] = useState(null);
  const hintStyles = cn('caption', styles.hintText);
  const panelStyles = cn('site-v2', className, {
    [styles.checked]: checkboxIsChecked,
  });
  const toggleHint = () => {
    setShowHint(!showHint);
  };
  const toggleCheckbox = (e) => {
    e.nativeEvent.stopImmediatePropagation();
    if (showHint) setDropdownValue(null);
    setShowHint(!showHint);
    setCheckboxIsChecked(!checkboxIsChecked);
  };
  const handleDropdownChange = (selectedValue) => {
    // { id: 5269, label: "I need the Parts (+$149)", value: 149 }
    const valueToRemove = dropdownValue ? dropdownValue.id : null;
    onDropdownChange({ add: selectedValue.id, remove: valueToRemove });
    setDropdownValue(selectedValue);
  };
  const imageRef = get(answer, 'additionalInfo.imageRef', null);
  const valueToShow = answer.value ? `+$${answer.value}` : null;
  const hasModalHint = Boolean(get(answer, 'additionalInfo.showModal', null));
  const showHintIcon = Boolean(hintText || hasModalHint);
  const additionalInfo = get(answer, 'additionalInfo', null);
  const showAdditionalInfo = additionalInfo && Boolean(Object.keys(additionalInfo).length);
  const getHintIconAction = () => {
    // This function assumes that a checkbox won't have both an inline hint and a modal launch
    // If we don't need to worry about toggling the hint then we can just pass the launch modal function directly instead of this
    if (hintText) return toggleHint;
    return () => {
      launchAdditionalInfoDisplay(null, answer.id);
    };
  };
  const hintIconAction = showHintIcon && getHintIconAction();

  return (
    <PanelV2 className={panelStyles} mediumBorderRadius>
      <div className="flex justifyContentSpaceBetween">
        <CheckboxLabel
          {...props}
          onClick={toggleCheckbox}
          checked={checkboxIsChecked}
          value={answer.value}
        />
        {showAdditionalInfo && (
          <PriceOrHintDisplay
            visible={Boolean(valueToShow || showHintIcon)}
            value={valueToShow}
            hintText={hintText}
            hasModalHint={hasModalHint}
            onHintClick={hintIconAction}
          />
        )}
      </div>
      {showDropdown && showHint && (
        <div className={styles.dropdown}>
          <SelectField
            {...props}
            value={dropdownValue}
            defaultValue=""
            size="sm"
            onChange={handleDropdownChange}
            arrowRenderer={DropdownArrow}
            placeholder="Do you need Parts?"
            className={!dropdownValue && dropdownClassName}
          />
        </div>
      )}
      {imageRef && (
        <div className="flex justifyContentCenter marginY-small2">
          <img src={imageRef} className={styles.imageRef} />
        </div>
      )}
      {showHint && hintText && <div className={hintStyles}>{hintText}</div>}
    </PanelV2>
  );
};

CheckboxLabel.propTypes = {
  checkboxValue: PropTypes.any,
  checkboxLabel: PropTypes.string,
  className: PropTypes.string,
  checked: PropTypes.bool,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
};

CheckboxPanel.propTypes = {
  hintText: PropTypes.string,
  dropdownClassName: PropTypes.string,
  onDropdownChange: PropTypes.func,
  dropdownValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  showDropdown: PropTypes.bool,
  className: PropTypes.string,
  launchAdditionalInfoDisplay: PropTypes.func,
  answer: PropTypes.shape({
    id: PropTypes.number,
    value: PropTypes.number,
    additionalInfo: PropTypes.shape({
      imageRef: PropTypes.string,
      content: PropTypes.string,
    }),
  }),
};
CheckboxPanel.defaultProps = {
  launchAdditionalInfoDisplay: () => {},
  answer: {},
};

PriceOrHintDisplay.propTypes = {
  visible: PropTypes.bool,
  value: PropTypes.string,
  onHintClick: PropTypes.func,
  hasModalHint: PropTypes.bool,
  hintText: PropTypes.string,
};
PriceOrHintDisplay.defaultProps = { onHintClick: () => {} };

export default React.memo(CheckboxPanel);
